const MaterialsEntityInputType = require('./PictProvider-InputType-MaterialsEntitySelector.js');

/**
 * Sample-type picker. Reuses the generic entity selector (Entity=SampleType) but keeps the sample's two
 * type columns in sync: the modern `IDSampleType` FK (set by the standard autofill) and the legacy
 * `MaterialType` code. On forward selection it mirrors the picked type's `Hash` onto `MaterialType`; when
 * seeding from a linked sample it prefers `IDSampleType` and falls back to resolving the `MaterialType`
 * code to a SampleType so pre-backfill samples still display (and pick up the FK on the next save).
 */
class SampleTypeSelectorInputType extends MaterialsEntityInputType
{
	/**
	 * @param {Object} pFable @param {Object} [pOptions] @param {string} [pServiceHash]
	 */
	constructor(pFable, pOptions, pServiceHash)
	{
		if (!pOptions)
		{
			pOptions = {};
		}
		if (!pOptions.Entity)
		{
 pOptions.Entity = 'SampleType';
		}
		super(pFable, pOptions, pServiceHash);
		/** @type {Record<string, any>|null} Hash -> SampleType record, lazily loaded for the reverse fallback. */
		this._sampleTypesByHash = null;
		/** @type {boolean} true while the map request is in flight; kept separate from the map so an unloaded map stays null. */
		this._sampleTypeMapLoading = false;
		/** @type {Array<{View: Object, Input: Object}>} inputs that initialized mid-load and owe a seed when the map lands. */
		this._sampleTypeMapWaiters = [];
		/** @type {boolean} re-entrancy guard so a fallback-driven re-marshal does not re-seed. */
		this._fallbackSeeding = false;
	}

	/** @param {Object} pInput @return {string} marshal-relative address of the sample's legacy MaterialType code. */
	_sampleMaterialTypeAddress(pInput)
	{
		return pInput?.PictForm?.SampleMaterialTypeAddress || 'SampleID__Record.MaterialType';
	}
	/** @param {Object} pInput @return {string} marshal-relative address of the sample's IDSampleType FK. */
	_sampleIDSampleTypeAddress(pInput)
	{
		return pInput?.PictForm?.SampleIDSampleTypeAddress || 'SampleID__Record.IDSampleType';
	}

	onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		const tmpResult = super.onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID);
		this._loadSampleTypeMap(pView, pInput);
		return tmpResult;
	}

	/**
	 * Lazily cache Hash -> SampleType for the current customer (row-level auth scopes the read).
	 *
	 * The load is async and the reverse-seed fires during init, so EVERY input that initializes while the
	 * request is in flight is queued and re-seeded when it lands — not just the first. The map itself stays
	 * `null` until it is real (the in-flight state is tracked separately), so the emptiness guard in
	 * `_seedSampleTypeFromMaterialType` means what it says: a seed during the window is suppressed rather
	 * than resolving against an empty map and silently giving up. An input arriving after the map is already
	 * loaded is seeded immediately — the init-time attempt that would otherwise cover it only runs off a
	 * record refresh, so a second form in the same session would never resolve.
	 *
	 * @param {Object} [pView] @param {Object} [pInput] the picker to seed once the map is available.
	 */
	_loadSampleTypeMap(pView, pInput)
	{
		if (!this.pict?.HeadlightRestClient)
		{
			return;
		}
		if (this._sampleTypesByHash)
		{
			if (pView && pInput)
			{
				this._seedSampleTypeFromMaterialType(pView, pInput);
			}
			return;
		}
		// The provider family is commonly instantiated via Object.create in harnesses and by the broker, so
		// do not assume the constructor ran.
		if (!Array.isArray(this._sampleTypeMapWaiters))
		{
			this._sampleTypeMapWaiters = [];
		}
		if (pView && pInput)
		{
			this._sampleTypeMapWaiters.push({ View: pView, Input: pInput });
		}
		if (this._sampleTypeMapLoading)
		{
			return;
		}
		this._sampleTypeMapLoading = true;
		this.pict.HeadlightRestClient.getJSON('SampleTypes/FilteredTo/FSF~IDSampleType~ASC~0/0/500', (pError, pTypes) =>
		{
			this._sampleTypeMapLoading = false;
			const tmpWaiters = this._sampleTypeMapWaiters;
			this._sampleTypeMapWaiters = [];
			if (pError || !Array.isArray(pTypes))
			{
				// Reset to null (not an empty map) so the next onInputInitialize retries rather than being
				// stuck resolving against a map that will never fill.
				this._sampleTypesByHash = null;
				return;
			}
			const tmpMap = {};
			for (const tmpType of pTypes)
			{
				if (tmpType && tmpType.Hash)
				{
					tmpMap[tmpType.Hash] = tmpType;
				}
			}
			this._sampleTypesByHash = tmpMap;
			for (const tmpWaiter of tmpWaiters)
			{
				this._seedSampleTypeFromMaterialType(tmpWaiter.View, tmpWaiter.Input);
			}
		});
	}

	/**
	 * @param {Object} pView @param {Object} pInput @param {any} pRecord @param {string} pRecordAddress
	 * @param {string} pTransactionGUID @param {number} [pRowIndex]
	 */
	_onRecordRefresh(pView, pInput, pRecord, pRecordAddress, pTransactionGUID, pRowIndex)
	{
		super._onRecordRefresh(pView, pInput, pRecord, pRecordAddress, pTransactionGUID, pRowIndex);
		if (pRecord && typeof pRecord === 'object' && !Array.isArray(pRecord) && pRecord.IDSampleType)
		{
			// Forward: mirror the picked type's Hash onto the sample's legacy MaterialType column.
			this.pict.manifest.setValueAtAddress(pView.getMarshalDestinationObject(), this._sampleMaterialTypeAddress(pInput), pRecord.Hash || '');
			return;
		}
		this._seedSampleTypeFromMaterialType(pView, pInput, pRecordAddress, pRowIndex);
	}

	/**
	 * Reverse fallback: when nothing is selected and the bound sample has no IDSampleType but carries a
	 * legacy MaterialType code, resolve it to a SampleType, seed the picker, and backfill the FK in-form.
	 * @param {Object} pView @param {Object} pInput @param {string} [pRecordAddress] @param {number} [pRowIndex]
	 */
	_seedSampleTypeFromMaterialType(pView, pInput, pRecordAddress, pRowIndex)
	{
		if (this._fallbackSeeding || !this._sampleTypesByHash)
		{
			return;
		}
		const tmpDestination = pView.getMarshalDestinationObject();
		const tmpExistingID = this.pict.manifest.getValueAtAddress(tmpDestination, this._sampleIDSampleTypeAddress(pInput));
		if (tmpExistingID)
		{
			return;
		}
		const tmpMaterialType = this.pict.manifest.getValueAtAddress(tmpDestination, this._sampleMaterialTypeAddress(pInput));
		const tmpResolved = tmpMaterialType ? this._sampleTypesByHash[tmpMaterialType] : null;
		if (!tmpResolved)
		{
			return;
		}
		const tmpRecordAddress = pRecordAddress || this._getRecordAddress(pView, pInput, pRowIndex);
		const tmpValueAddress = tmpRecordAddress.replace(/__Record$/, '');
		this._fallbackSeeding = true;
		try
		{
			this.pict.manifest.setValueAtAddress(tmpDestination, this._sampleIDSampleTypeAddress(pInput), tmpResolved.IDSampleType);
			this.pict.manifest.setValueAtAddress(tmpDestination, tmpValueAddress, tmpResolved.IDSampleType);
			this.pict.manifest.setValueAtAddress(tmpDestination, tmpRecordAddress, tmpResolved);
			if (typeof pView.marshalToView === 'function')
			{
				pView.marshalToView();
			}
		}
		finally
		{
			this._fallbackSeeding = false;
		}
	}
}

module.exports = SampleTypeSelectorInputType;
